// Centralized service metadata for detail pages & listings
// Each service includes SEO-friendly slug, descriptive copy blocks and CTA helpers.

export const servicesData = [
  {
    title: 'Vehicle Repair & Maintenance',
    slug: 'vehicle-repair-maintenance',
    tagline: 'Structured lifecycle care preserving reliability & long-term value.',
    summary: 'Comprehensive scheduled and corrective maintenance programs aligning with OEM interval logic while adapting to local operating conditions, driving profile and vehicle age.',
    benefits: [
      'OEM interval alignment with adaptive adjustments',
      'Preventive detection reduces major failure risk',
      'Fluid, filter & wear-item integrity tracking',
      'Lifecycle preservation & resale value support'
    ],
    inclusions: [
      'Oil & filter strategy optimization',
      'Brake pad / disc condition reporting',
      'Battery load & health testing',
      'Belt / hose & leakage visual scan',
      'Cooling & lubrication system check',
      'Chassis & underbody glance assessment'
    ],
    symptoms: [
      'Extended start times',
      'Inconsistent idle or hesitation',
      'Brake fade or vibration',
      'Visible leaks or fluid seepage'
    ],
    ctaWhatsAppText: 'I would like to schedule Vehicle Repair & Maintenance'
  },
  {
    title: 'Car Scanning & Diagnostics',
    slug: 'car-scanning-diagnostics',
    tagline: 'Data-driven fault isolation using advanced scan tooling.',
    summary: 'We interrogate control modules, analyze live data streams, perform fault code correlation and validate root causes before any parts are recommended—reducing misdiagnosis cost.',
    benefits: [
      'Module communication integrity checks',
      'Live parameter & sensor value trending',
      'Fault code correlation & freeze frame review',
      'Targeted test plan reduces guesswork'
    ],
    inclusions: [
      'OBD / advanced platform scan',
      'Module readiness & adaptation status',
      'Sensor plausibility screening',
      'Electrical pathway preliminary assessment',
      'Report + recommended next steps'
    ],
    symptoms: [
      'Warning lights illuminated',
      'Intermittent performance issues',
      'Unusual fuel consumption shifts',
      'Harsh shifting or limp mode'
    ],
    ctaWhatsAppText: 'I would like a diagnostic scan appointment'
  },
  {
    title: 'Major & Minor Service',
    slug: 'major-minor-service',
    tagline: 'Interval-based service tiers matched to real vehicle condition.',
    summary: 'Execution of minor and major interval services including fluid renewal, filtration, dynamic systems check and health reporting—balancing preventive diligence with cost efficiency.',
    benefits: [
      'Maintains warranty & mechanical integrity',
      'Minimizes unplanned downtime risk',
      'Structured inspection & reporting protocol',
      'Engine efficiency & emissions stability'
    ],
    inclusions: [
      'Engine oil & filter replacement',
      'Air / cabin / fuel filter (interval-based)',
      'Spark plug inspection / replacement (interval)',
      'Brake visual & thickness measurement',
      'Fluid top-up & condition notation',
      'Digital service record entry'
    ],
    symptoms: [
      'Service indicator active',
      'Oil degradation warning',
      'Reduced fuel efficiency',
      'Noticeable drivability change'
    ],
    ctaWhatsAppText: 'I would like to book a major or minor service'
  },
  {
    title: 'Car AC Services',
    slug: 'car-ac-services',
    tagline: 'Cabin climate optimization & cooling performance recovery.',
    summary: 'We restore cooling efficiency through leak tracing, refrigerant balancing, airflow component inspection and hygiene-focused filtration to ensure stable interior comfort.',
    benefits: [
      'Improved vent temperature stability',
      'Reduced compressor load & drag',
      'Cleaner cabin air circulation',
      'Leak prevention & longevity'
    ],
    inclusions: [
      'AC pressure readings & balance check',
      'Evaporator / condenser visual inspection',
      'Cabin filter cleaning or replacement',
      'Vent temperature differential test',
      'Refrigerant level assessment',
      'Leak detection (dye or electronic)'
    ],
    symptoms: [
      'Warm airflow under load',
      'Musty odors on startup',
      'AC cycles too frequently',
      'Fogging or airflow reduction'
    ],
    ctaWhatsAppText: 'I would like to check my AC performance'
  },
  {
    title: 'Premium Oil Services',
    slug: 'premium-oil-services',
    tagline: 'High-spec lubrication strategy matched to platform & usage.',
    summary: 'We specify premium-grade lubricants and filtration based on engine design, thermal behavior, local climate and driving pattern—extending protection margins and efficiency.',
    benefits: [
      'Reduced internal wear & deposits',
      'Stable viscosity across temperature range',
      'Extended protection under load',
      'Optimized fuel & thermal efficiency'
    ],
    inclusions: [
      'Oil spec suitability verification',
      'Filter media quality assurance',
      'Drain strategy / contamination check',
      'Service interval calibration guidance',
      'Optional additive consultation'
    ],
    symptoms: [
      'Oil consumption increase',
      'Noisy cold starts',
      'Oil pressure warnings',
      'Dark / sludgy oil appearance'
    ],
    ctaWhatsAppText: 'I would like a premium oil service'
  },
  {
    title: 'Car Denting & Painting',
    slug: 'car-denting-painting',
    tagline: 'Panel restoration & OEM-grade refinishing.',
    summary: 'Cosmetic and structural panel correction, surface preparation, color matching and staged refinishing returning consistent OEM visual alignment and gloss depth.',
    benefits: [
      'Resale value & presentation uplift',
      'Corrosion risk mitigation',
      'Accurate paint code color matching',
      'Refined finish & surface consistency'
    ],
    inclusions: [
      'Panel assessment & straightening plan',
      'Surface prep & filler shaping',
      'Base / clear application & curing',
      'Color blend & edge transition control',
      'Final polishing & inspection'
    ],
    symptoms: [
      'Visible dents or creases',
      'Clear coat failure / peel',
      'Color fade or mismatch',
      'Post-collision aesthetic damage'
    ],
    ctaWhatsAppText: 'I would like denting & painting assistance'
  },
  {
    title: 'Car Battery Services',
    slug: 'car-battery-services',
    tagline: 'Starting system reliability & electrical stability.',
    summary: 'Testing, capacity validation and proper specification replacement ensures stable cold starts, voltage delivery and ECU / module protection from low-voltage events.',
    benefits: [
      'Reduced unexpected no-start events',
      'Stable cranking voltage profile',
      'Protection of sensitive electronics',
      'Correct battery spec & coding (if req.)'
    ],
    inclusions: [
      'Load & reserve capacity testing',
      'Terminal & cable corrosion cleaning',
      'Charging system quick evaluation',
      'Proper venting / mounting verification',
      'Battery replacement & disposal'
    ],
    symptoms: [
      'Slow or hesitant cranking',
      'Electrical glitches / resets',
      'Repeated jump start reliance',
      'Battery warning light'
    ],
    ctaWhatsAppText: 'I would like a battery test or replacement'
  },
  {
    title: 'Car Brakes Services',
    slug: 'car-brakes-services',
    tagline: 'Stopping power restoration & pedal feel integrity.',
    summary: 'Inspection and renewal of pad, rotor and hydraulic elements ensure progressive, predictable braking response with minimized vibration and fade risk.',
    benefits: [
      'Shorter, more consistent stopping distances',
      'Improved pedal modulation',
      'Reduced pulsation or squeal issues',
      'Optimized component longevity'
    ],
    inclusions: [
      'Pad thickness & wear pattern analysis',
      'Rotor surface & runout check',
      'Brake fluid level & condition note',
      'Line / hose quick visual integrity check',
      'Caliper function observation'
    ],
    symptoms: [
      'Squealing or grinding noises',
      'Steering vibration during braking',
      'Soft or spongy pedal feel',
      'Brake warning light'
    ],
    ctaWhatsAppText: 'I would like a brake system inspection'
  },
  {
    title: 'Car Brakes & Suspension',
    slug: 'car-brakes-suspension',
    tagline: 'Integrated ride control, stability & braking synergy.',
    summary: 'Holistic assessment of suspension geometry, bushing condition, damping behavior and braking system interaction for safe, balanced dynamics.',
    benefits: [
      'Improved directional stability',
      'Reduced uneven tire wear',
      'Enhanced ride comfort & control',
      'Balanced braking under load'
    ],
    inclusions: [
      'Shock / strut leakage & bounce test',
      'Control arm & bushing wear review',
      'Steering & joint play check',
      'Brake balance & hardware correlation',
      'Alignment status advisory'
    ],
    symptoms: [
      'Excessive body roll or float',
      'Uneven tire shoulder wear',
      'Nose dive on braking',
      'Clunks over bumps'
    ],
    ctaWhatsAppText: 'I would like a suspension & brake evaluation'
  },
  {
    title: 'Car Engine Repair',
    slug: 'car-engine-repair',
    tagline: 'Mechanical restoration & sealing integrity recovery.',
    summary: 'From top-end sealing to rotating assembly integrity, we address mechanical wear sources, drivability loss and efficiency degradation at component level.',
    benefits: [
      'Extended engine service life',
      'Compression & sealing stability',
      'Reduced oil consumption or leakage',
      'Restored thermal & combustion efficiency'
    ],
    inclusions: [
      'Compression / leak-down guidance (if req.)',
      'Gasket & seal integrity planning',
      'Component condition documentation',
      'Fluids & contamination advisory',
      'Post-repair validation routine'
    ],
    symptoms: [
      'Misfires or rough idle',
      'Oil leakage / consumption',
      'Power loss under load',
      'Excessive exhaust smoke'
    ],
    ctaWhatsAppText: 'I would like engine repair assistance'
  },
  {
    title: 'Car Transmission Repair',
    slug: 'car-transmission-repair',
    tagline: 'Driveline continuity & shift quality refinement.',
    summary: 'Assessment and repair coordination for manual & automatic transmission issues including shift quality, hydraulic performance, fluid integrity and control logic.',
    benefits: [
      'Restored shift smoothness',
      'Reduced shock / flare events',
      'Lower overheating risk',
      'Protection of clutch / band components'
    ],
    inclusions: [
      'Fluid condition & level assessment',
      'External seal & case inspection',
      'Mount & linkage visual check',
      'Preliminary adaptive behavior note',
      'Post-fix verification guidance'
    ],
    symptoms: [
      'Harsh / delayed shifting',
      'Slipping under acceleration',
      'Fluid discoloration or burnt odor',
      'Transmission warning light'
    ],
    ctaWhatsAppText: 'I would like transmission repair assistance'
  },
  {
    title: 'Tyre Replacement',
    slug: 'tyre-replacement',
    tagline: 'Optimized grip, safety & wear uniformity.',
    summary: 'Selection, mounting and balancing of properly specified tires aligned with intended usage, performance envelope and alignment status for safe predictable handling.',
    benefits: [
      'Improved wet & dry traction',
      'Reduced braking distances',
      'Balanced tread wear progression',
      'Ride quality & vibration reduction'
    ],
    inclusions: [
      'Size & load spec verification',
      'Tread wear pattern advisory',
      'Mounting & dynamic balancing',
      'Valve / TPMS handling care',
      'Post-install torque check'
    ],
    symptoms: [
      'Vibration at speed',
      'Visible tread cupping / feathering',
      'Reduced wet confidence',
      'Increased stopping distance'
    ],
    ctaWhatsAppText: 'I would like new tyres or inspection'
  }
];

export function getServiceBySlug(slug) {
  return servicesData.find(s => s.slug === slug);
}
